import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, ChevronRight, Search } from 'lucide-react';
import { orderService } from '@/services/order.service';
import { Order } from '@/types';
import { OrderStatusBadge } from '@/components/common/OrderStatusBadge';
import { OrderCardSkeleton } from '@/components/common/Skeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { formatCurrency, formatDate } from '@/utils';

const STATUS_TABS = ['All', 'PENDING', 'CONFIRMED', 'PROCESSING', 'SHIPPED', 'DELIVERED', 'CANCELLED'];

export const OrdersPage = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('All');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setLoading(true);
    orderService.getUserOrders(page, activeTab === 'All' ? undefined : activeTab)
      .then(res => {
        setOrders(res.data.data);
        setTotal(res.data.pagination?.total || 0);
      })
      .finally(() => setLoading(false));
  }, [activeTab, page]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="font-heading font-bold text-2xl text-gray-800 mb-6">My Orders</h1>

      {/* Status Tabs */}
      <div className="flex gap-1 overflow-x-auto scrollbar-none mb-6 bg-white rounded-xl shadow-card p-1">
        {STATUS_TABS.map(tab => (
          <button
            key={tab}
            onClick={() => { setActiveTab(tab); setPage(1); }}
            className={`whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab ? 'bg-primary-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
          >
            {tab === 'All' ? 'All Orders' : tab.charAt(0) + tab.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-4">{Array(5).fill(0).map((_, i) => <OrderCardSkeleton key={i} />)}</div>
      ) : orders.length === 0 ? (
        <EmptyState
          icon={Package}
          title="No orders yet"
          description="When you place orders, they'll appear here."
          action={{ label: 'Start Shopping', onClick: () => window.location.href = '/' }}
        />
      ) : (
        <div className="space-y-4">
          {orders.map(order => (
            <Link key={order.id} to={`/orders/${order.id}`} className="block bg-white rounded-card shadow-card hover:shadow-card-hover transition-all p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center shrink-0">
                    <Package className="h-6 w-6 text-primary-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">#{order.orderNumber}</p>
                    <p className="text-xs text-gray-400">{formatDate(order.createdAt)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <OrderStatusBadge status={order.status} />
                  <p className="font-bold text-gray-800 mt-1">{formatCurrency(Number(order.total))}</p>
                </div>
              </div>

              <div className="mt-4 flex items-center gap-3 overflow-x-auto">
                {order.items?.slice(0, 4).map(item => (
                  <img
                    key={item.id}
                    src={item.product?.images?.[0] || '/placeholder-product.jpg'}
                    alt={item.product?.name}
                    className="w-12 h-12 object-cover rounded-lg shrink-0"
                  />
                ))}
                {(order.items?.length || 0) > 4 && (
                  <span className="text-sm text-gray-400 shrink-0">+{order.items.length - 4} more</span>
                )}
                <ChevronRight className="h-4 w-4 text-gray-400 ml-auto shrink-0" />
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > 10 && (
        <div className="flex justify-center gap-2 mt-8">
          {Array.from({ length: Math.ceil(total / 10) }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => setPage(i + 1)}
              className={`w-9 h-9 rounded-xl text-sm font-medium transition-all ${page === i + 1 ? 'bg-primary-500 text-white' : 'bg-white border border-gray-200 text-gray-700 hover:border-primary-300'}`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
